import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import ProcessCardsRow from '../components/ProcessCardsRow';
import PricingGuaranteeCard from '../components/PricingGuaranteeCard';
import AuthModal from '../components/modals/AuthModal';

const sway = keyframes`
  0% { transform: rotate(-12deg) rotateY(6deg) translateY(-4px); }
  50% { transform: rotate(-9deg) rotateY(-6deg) translateY(4px); }
  100% { transform: rotate(-6deg) rotateY(6deg) translateY(-4px); }
`;

const Wrapper = styled.section`
  scroll-snap-align: start;
  min-height: 100vh;
  padding: 128px 1.5rem 96px;
  background: #efecf0;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #000;
  position: relative;
  overflow-x: clip;
`;

const Inner = styled.div`
  width: 100%;
  max-width: 1540px;
  display: grid;
  gap: 3rem;
  margin-bottom: 2.75rem;

  @media (min-width: 900px) {
    grid-template-columns: 0.8fr 1.2fr;
    align-items: center;
  }
`;

const Eyebrow = styled.span`
  display: block;
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-size: clamp(2.75rem, 6vw, 4rem);
  line-height: 1.05;
  margin: 0;
  font-weight: 700;
  text-align: center;
`;

const Subtitle = styled.h3`
  font-family: serif;
  font-style: italic;
  font-weight: 400;
  font-size: clamp(1.5rem, 4vw, 2.5rem);
  line-height: 1.1;
  margin: 0.25em 0 clamp(3.5rem, 7vw, 5.5rem);
  text-align: center;
`;

const JoinCard = styled.div`
  background: #fff url('/images/cardboardTexture.jpg') center/cover no-repeat;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 100%;
  min-height: 580px;
  padding: 3rem 2.5rem 4rem;
  position: relative;
  box-shadow:
    0 14px 28px rgba(0, 0, 0, 0.12),
    0 34px 54px rgba(0, 0, 0, 0.2);
  isolation: isolate;

  @media (max-width: 900px) {
    min-height: 480px;
  }
`;

const GiftCardWrapper = styled.div`
  position: absolute;
  top: -80px;
  left: -56px;
  width: 480px;
  height: 290px;
  perspective: 1000px;
  z-index: 3;
  pointer-events: none;

  @media (prefers-reduced-motion: reduce) {
    min-width: 370px;
    width: auto;
    right: -24%;
  }

  @media (max-width: 900px) {
    top: 20px;
    left: -10px;
    width: 260px;
    height: 150px;
  }
`;

const GiftCard = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  will-change: transform, box-shadow;
  background: url('/images/rubberDuck.png') center/cover no-repeat;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
  animation: ${sway} 9s ease-in-out infinite alternate;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const JoinContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.35rem;
  width: min(100%, 240px);
  position: relative;
  z-index: 2;
`;

const JoinHeading = styled.h4`
  font-size: 2.25rem;
  font-weight: 600;
  line-height: 1.15;
  margin: 0;
`;

const JoinCta = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  min-height: 48px;
  padding: 0.82rem 1.2rem;
  border-radius: 999px;
  border: 1px solid rgba(23, 33, 47, 0.14);
  background: rgba(255, 255, 255, 0.82);
  color: #17212f;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  box-shadow:
    0 10px 24px rgba(0, 0, 0, 0.08),
    0 2px 6px rgba(0, 0, 0, 0.08);
  transition:
    transform 160ms ease,
    box-shadow 160ms ease,
    background-color 160ms ease,
    border-color 160ms ease;

  &:hover {
    transform: translateY(-1px);
    background: rgba(255, 255, 255, 0.96);
    border-color: rgba(23, 33, 47, 0.22);
    box-shadow:
      0 14px 30px rgba(0, 0, 0, 0.1),
      0 3px 8px rgba(0, 0, 0, 0.08);
  }
`;

type RequestExample = {
  readonly title: string;
  readonly detail: string;
};

const requestExamples: readonly RequestExample[] = [
  {
    title: 'UI / UX Design',
    detail: 'Wireframes, polished screens, flows, and product experiences.',
  },
  {
    title: 'Logo Concepts',
    detail: 'Identity directions, lockups, and refinements.',
  },
  {
    title: 'Custom Icons',
    detail: 'Branded icon systems for apps, products, and marketing.',
  },
  {
    title: 'Photo Touchups',
    detail: 'Retouching, cleanup, cutouts, and color correction.',
  },
  {
    title: 'Landing Pages',
    detail: 'Campaign layouts designed for launches and conversions.',
  },
  {
    title: 'Ad Creative',
    detail: 'Static ads, social variations, and promo graphics.',
  },
  {
    title: 'Email Graphics',
    detail: 'Headers, modules, and branded campaign visuals.',
  },
  {
    title: 'Presentation Design',
    detail: 'Decks, sales assets, and branded slide systems.',
  },
] as const;

const RequestExamples = styled.div`
  min-height: 580px;
  display: grid;
  align-content: start;
  gap: 1.4rem;
  padding-top: 0.35rem;

  @media (max-width: 900px) {
    min-height: auto;
  }
`;

const RequestPanelHeader = styled.div`
  display: grid;
  gap: 0.4rem;
`;

const RequestPanelEyebrow = styled.span`
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(23, 33, 47, 0.56);
`;

const RequestPanelTitle = styled.h4`
  margin: 0;
  font-size: clamp(1.5rem, 2.4vw, 2rem);
  line-height: 1.02;
  letter-spacing: -0.03em;
  color: #17212f;
`;

const RequestPanelLead = styled.p`
  margin: 0;
  color: #465263;
  font-size: 0.98rem;
  line-height: 1.6;
`;

const RequestList = styled.div`
  display: grid;
  gap: 1rem 2rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const RequestItem = styled.article`
  display: grid;
  align-content: start;
  gap: 0.45rem;
  padding: 0 0 0.95rem;
  border-bottom: 1px solid rgba(23, 33, 47, 0.1);
`;

const RequestItemTitle = styled.h5`
  margin: 0;
  color: #17212f;
  font-size: 1.1rem;
  line-height: 1.05;
  letter-spacing: -0.03em;
`;

const RequestItemDetail = styled.p`
  margin: 0;
  color: #465263;
  font-size: 0.92rem;
  line-height: 1.55;
`;

const HowItWorksHeading = styled.h3`
  margin: 0;
  color: #17212f;
  font-size: clamp(1.35rem, 2.4vw, 2rem);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.03em;
  text-align: center;
`;

const ProcessCardsShelf = styled(ProcessCardsRow)`
  width: 100%;
  margin: clamp(1.75rem, 4vw, 3.25rem) auto 5rem;
`;

export default function PricingSection() {
  const [authOpen, setAuthOpen] = useState(false);

  return (
    <Wrapper id="pricing">
      <Eyebrow>Cybard Club</Eyebrow>
      <Title>Unlimited Assets and Design Requests</Title>
      <Subtitle>for endless&nbsp;possibilities</Subtitle>

      <Inner>
        <JoinCard>
          <GiftCardWrapper>
            <GiftCard />
          </GiftCardWrapper>
          <JoinContent>
            <JoinHeading>
              Join <br />
              CyBard
            </JoinHeading>
            <JoinCta to="/services/assets-design">Join today</JoinCta>
          </JoinContent>
        </JoinCard>

        <RequestExamples>
          <RequestPanelHeader>
            <RequestPanelEyebrow>Request Examples</RequestPanelEyebrow>
            <RequestPanelTitle>Examples of what you can hand off.</RequestPanelTitle>
            <RequestPanelLead>
              Design support can cover everything from product screens and logos to photo cleanup,
              icon sets, campaign graphics, and presentation work.
            </RequestPanelLead>
          </RequestPanelHeader>

          <RequestList>
            {requestExamples.map((example) => (
              <RequestItem key={example.title}>
                <RequestItemTitle>{example.title}</RequestItemTitle>
                <RequestItemDetail>{example.detail}</RequestItemDetail>
              </RequestItem>
            ))}
          </RequestList>
        </RequestExamples>
      </Inner>

      <HowItWorksHeading>How it works</HowItWorksHeading>

      <ProcessCardsShelf hideSubscribePrice />

      <PricingGuaranteeCard />

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </Wrapper>
  );
}
